import { useEffect, useRef, useState } from 'react';
import { Share, X } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'install-banner-dismissed';

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as { MSStream?: unknown }).MSStream;
}

export function InstallBanner() {
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const promptRef = useRef<any>(null);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onIos = isIOS();
    setIos(onIos);

    if (onIos) {
      setShow(true);
      return;
    }

    // Android / Chrome: wait for the browser's install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      promptRef.current = e;
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (!promptRef.current) return;
    promptRef.current.prompt();
    await promptRef.current.userChoice;
    setShow(false);
  }

  function handleDismiss() {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  }

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 rounded-xl border border-border bg-card shadow-lg p-4">
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
        aria-label="Schließen"
      >
        <X size={16} />
      </button>

      {ios ? (
        <p className="text-sm pr-6 leading-relaxed">
          Tippe auf{' '}
          <Share size={14} className="inline-block align-text-bottom mx-0.5" />
          {' '}und dann{' '}
          <strong>„Zum Home-Bildschirm"</strong>, um die App zu installieren.
        </p>
      ) : (
        <div className="pr-6 space-y-2">
          <p className="text-sm font-medium">App installieren</p>
          <p className="text-xs text-muted-foreground">
            Füge die App zum Home-Bildschirm hinzu für das beste Erlebnis.
          </p>
          <Button size="sm" onClick={handleInstall}>
            Installieren
          </Button>
        </div>
      )}
    </div>
  );
}
